import { Injectable } from "@angular/core";
import { DateTime } from "luxon";
import { Audit, AuditStatus } from "@/_models/audit.interface";
import { Issue, SeverityState } from "@/_models/issue.interface";
import {
    Promotion,
    PromotionStatus,
    PromotionType,
    getDescriptionForType,
} from "@/_models/promotion.interface";
import { RiskEvaluation } from "@/_models/risk-evaluation.model";
import { isTaskOverdue, Task, TaskStatus } from "@/_models/task.interface";
import { FlightModel } from "@/_models/flight/flight.model";
import { riskLevelDisplayLabel } from "@/_models/risk-authority.util";
import { findingSeverityLabel } from "@/_models/finding-severity.util";
import { AuthenticationService } from "@/_services/authentication.service";
import { TerminologyService } from "@/_services/terminology/terminology.service";
import { DateUtils } from "@/_utility/DateTimeFormat";
import { toDateTime } from "@/_utility/date.utils";
import { listItemId } from "@/_utility/list-item-title.util";

export interface AssignedItemMeta {
    publicId: string;
    context: string;
    date: string;
}

export const EMPTY_ASSIGNED_ITEM_META: AssignedItemMeta = {
    publicId: "",
    context: "",
    date: "",
};

const ASSESSED_ACTIVITY_FORM_TYPE = "assessment";
const REPORT_DATE_PREFIX = "Dated";
const INCIDENT_DATE_PREFIX = "Occurred";
const DUE_DATE_PREFIX = "Due";
const OVERDUE_DATE_PREFIX = "Overdue";
const REVIEW_DATE_PREFIX = "Review";
const REVIEW_OVERDUE_DATE_PREFIX = "Review overdue";
const IN_PROGRESS_AUDIT_STATUSES: readonly string[] = [
    AuditStatus.Scheduled,
    AuditStatus.InProgress,
];

@Injectable({
    providedIn: "root",
})
export class AssignedItemMetaService {
    constructor(
        private dateUtils: DateUtils,
        private terminology: TerminologyService,
        private authentication: AuthenticationService,
    ) {}

    forPromotion(promotion: Promotion | null | undefined): AssignedItemMeta {
        if (!promotion) {
            return EMPTY_ASSIGNED_ITEM_META;
        }
        return {
            publicId: listItemId(promotion),
            context: promotion.type
                ? getDescriptionForType(promotion.type as PromotionType)
                : "",
            date: this.promotionDate(promotion),
        };
    }

    forCase(issue: Issue | null | undefined): AssignedItemMeta {
        if (!issue) {
            return EMPTY_ASSIGNED_ITEM_META;
        }
        return {
            publicId: listItemId(issue),
            context: this.severityLabel(issue),
            date: issue.submissionDate
                ? this.prefixed("Submitted", issue.submissionDate)
                : this.prefixed(INCIDENT_DATE_PREFIX, issue.incidentDate),
        };
    }

    forTask(task: Task | null | undefined): AssignedItemMeta {
        if (!task) {
            return EMPTY_ASSIGNED_ITEM_META;
        }
        return {
            publicId: listItemId(task),
            context: task.priority ?? "",
            date: this.prefixed(
                isTaskOverdue(task) ? OVERDUE_DATE_PREFIX : DUE_DATE_PREFIX,
                task.dueDate,
            ),
        };
    }

    forAudit(audit: Audit | null | undefined): AssignedItemMeta {
        if (!audit) {
            return EMPTY_ASSIGNED_ITEM_META;
        }
        return {
            publicId: listItemId(audit),
            context: audit.category ?? audit.template?.name ?? "",
            date: this.prefixed(
                this.isAuditOverdue(audit)
                    ? OVERDUE_DATE_PREFIX
                    : DUE_DATE_PREFIX,
                audit.dueDate,
            ),
        };
    }

    forRiskEvaluation(
        riskEvaluation: RiskEvaluation | null | undefined,
    ): AssignedItemMeta {
        if (!riskEvaluation) {
            return EMPTY_ASSIGNED_ITEM_META;
        }
        return {
            publicId: listItemId(riskEvaluation),
            context: riskLevelDisplayLabel(
                riskEvaluation.residualAssessment?.riskLevel,
            ),
            date: this.prefixed(
                this.isPastDue(riskEvaluation.acceptance?.nextReviewDate)
                    ? REVIEW_OVERDUE_DATE_PREFIX
                    : REVIEW_DATE_PREFIX,
                riskEvaluation.acceptance?.nextReviewDate,
            ),
        };
    }

    reportDate(flight: FlightModel | null | undefined): string {
        return this.prefixed(
            this.reportDatePrefix(flight?.assessment?.formTypeSettings?.type),
            flight?.flightDate,
            "UTC",
        );
    }

    private promotionDate(promotion: Promotion): string {
        if (promotion.status === PromotionStatus.Overdue && promotion.dueDate) {
            return this.prefixed("Overdue", promotion.dueDate);
        }
        if (this.isUnsentDraft(promotion)) {
            return "Draft, not sent";
        }
        return this.prefixed("Sent", promotion.sentDate);
    }

    private isUnsentDraft(promotion: Promotion): boolean {
        const currentUserId = this.authentication.currentUserValue?._id;
        return (
            !!currentUserId &&
            currentUserId === promotion.creator?._id &&
            promotion.status === PromotionStatus.Open
        );
    }

    private reportDatePrefix(formType: string | null | undefined): string {
        return formType === ASSESSED_ACTIVITY_FORM_TYPE
            ? this.terminology.resolve(
                  "dashboard.assignedItems.date.assessment",
              )
            : REPORT_DATE_PREFIX;
    }

    private severityLabel(issue: Issue): string {
        if (issue.findingSeverity) {
            return findingSeverityLabel(issue.findingSeverity);
        }
        return issue.severity == null
            ? ""
            : SeverityState[issue.severity] ?? "";
    }

    private isAuditOverdue(audit: Audit): boolean {
        return (
            audit.status === AuditStatus.Overdue ||
            (IN_PROGRESS_AUDIT_STATUSES.includes(audit.status) &&
                this.isPastDue(audit.dueDate))
        );
    }

    private isPastDue(date: Date | string | null | undefined): boolean {
        return !!date && toDateTime(date) < DateTime.now();
    }

    private prefixed(
        prefix: string,
        date: Date | string | null | undefined,
        timeZone?: "UTC",
    ): string {
        const formatted = date
            ? this.dateUtils.formatDateWithConditionalYear(date, timeZone)
            : "";
        return formatted ? `${prefix} ${formatted}` : "";
    }
}
